import { useEffect, useRef } from "react";

/**
 * Cloudflare Turnstile widget (anti-bot) for the OTP / registration forms.
 *
 * Feature-flagged by VITE_TURNSTILE_SITE_KEY: if it's not set, the component renders
 * nothing and `isTurnstileEnabled()` returns false, so forms behave exactly as before.
 * When enabled, it renders the managed widget and calls `onToken` with the token
 * (or null on expiry/error). Use a changing `resetKey` prop to force a fresh token
 * (Turnstile tokens are single-use — bump it after each submit / for "resend").
 */

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id?: string) => void;
    };
  }
}

export function isTurnstileEnabled(): boolean {
  return !!SITE_KEY;
}

function ensureScript(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve();
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      // it may already be loaded
      if (window.turnstile) resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

interface TurnstileProps {
  onToken: (token: string | null) => void;
  resetKey?: number;
  className?: string;
}

export function Turnstile({ onToken, resetKey = 0, className }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // keep the latest callback without re-running the render effect
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!SITE_KEY || typeof window === "undefined") return;
    let cancelled = false;

    ensureScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      // clear any previous render in this container
      containerRef.current.innerHTML = "";
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(null),
        "error-callback": () => onTokenRef.current(null),
        theme: "light",
        // invisible for legitimate users; CF only shows a challenge if it's suspicious
        appearance: "interaction-only",
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* noop */
        }
        widgetIdRef.current = null;
      }
    };
    // re-render (fresh token) whenever resetKey changes
  }, [resetKey]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} className={className} />;
}
